import type { ReactElement } from "react";
import { Link } from "react-router";
import { GRADIENT_AUTH_BUTTON_CLASSES, UserMenu } from "@/app/layout/user-menu";
import { useAuth } from "@/core/auth/auth-provider";
import { Button } from "@/shared/components/ui";

/**
 * Branded header for public unauthenticated surfaces (FR-040): the same
 * approved gradient/height/logo as the authenticated shell header, minus the
 * sidebar navigation toggle. Unauthenticated visitors get a Sign in entry
 * point; authenticated users who reach a public page (e.g. /home/* from the
 * app footer) keep the shell's profile controls. While the session is still
 * resolving (initial /auth/me) or a sign-out is in flight, the auth slot is a
 * non-actionable placeholder so users never see controls for the wrong state.
 */
export function PublicHeader(): ReactElement {
  const auth = useAuth();
  // "loading" = initial session fetch; "logout" = sign-out in flight. Both
  // are transitional: rendering Sign in or profile controls here would offer
  // an action for a state that may be wrong a moment later.
  const isTransitional = auth.status === "loading" || auth.status === "logout";
  const isAuthenticated = auth.session?.authenticated === true;

  return (
    <header
      role="banner"
      className="sticky top-0 z-30 bg-gradient-to-r from-[#1893D1] to-[#16456B]"
    >
      <div className="clm-page-gutter flex h-[54px] items-center justify-between gap-4 text-white">
        <Link
          to="/"
          className="flex items-center gap-2 no-underline hover:no-underline"
          aria-label="CLM Web home"
        >
          <img
            src="/logos/header-logo.svg"
            alt="CLM Web logo"
            className="h-[30px] w-auto"
            loading="eager"
            width={74}
            height={30}
          />
        </Link>
        {isTransitional ? (
          // Reserves the auth-slot footprint (sm button height) so the header
          // does not shift when the resolved control mounts.
          <span aria-hidden="true" className="inline-block h-8 w-20" />
        ) : isAuthenticated ? (
          <UserMenu />
        ) : (
          // White-outline treatment matching the Sign out button (user-menu.tsx)
          // per the 2026-08-22 product ruling (Figma navigation sheets / CLM
          // wiki design source). Supersedes the 2026-08-07 accessibility-ruling
          // Secondary treatment for this control; the 2026-08-24 gradient-
          // direction ruling resolved the former contrast deviation — the dark
          // #16456B end now backs this control (≈10:1, AA pass) — see
          // docs/accessibility-audit.md addendum. The underline suppression
          // keeps the asChild anchor visually identical to the Sign out <button>.
          <Button
            asChild
            variant="outline"
            size="sm"
            className={`${GRADIENT_AUTH_BUTTON_CLASSES} no-underline hover:no-underline`}
          >
            <Link to="/login">Sign in</Link>
          </Button>
        )}
      </div>
    </header>
  );
}
